// Kırılım penceresi boyut seçici (spec §19 B3/B4/B2): soru biçimli etiket "Neye göre kırılıyor?" +
// tek satır açıklama, üç bölümlü aranabilir liste (uygun · diğer · sizin tanımladıklarınız), seçilen
// boyutun görünür yardım satırı (ne zaman + örnek üyeler). Kod (slug) yalnız "Diğer"de görünür:
// boyut adı + addan üretilen düzenlenebilir kod + benzer boyut uyarısı (engellemez; "Onu seç").
// Benzer ad: yerel kural anında, sunucu (`?similarTo=`, 400 ms gecikmeli) geldiğinde onu kullanır.

import { useEffect, useMemo, useState, type CSSProperties } from "react";
import type { KpiRootCode } from "@mahir/shared";
import { listKpiBreakdownDimensions } from "../../api/kpi";
import { KPI_BREAKDOWN_COPY } from "../../lib/kpi-breakdown-copy";
import {
  choiceForValue,
  dimensionHelp,
  dimensionPickerOptions,
  otherDimensionState,
  similarHint,
  type CustomDimensionOption,
  type DimensionChoice,
} from "../../lib/kpi-breakdown-dimension-ui";
import { type GroupForm } from "../../lib/kpi-breakdown-group-form";
import { SearchSelect } from "../common/SearchSelect";
import { btnGhost, fieldStyle, inputStyle, labelStyle } from "./kpi-form-styles";

const C = KPI_BREAKDOWN_COPY.modal;
const OTHER = "diger";
const SIMILAR_DEBOUNCE_MS = 400;
const help: CSSProperties = { fontSize: "0.875rem", color: "var(--color-text-3)" };
const question: CSSProperties = {
  fontFamily: "var(--font-sans)",
  fontSize: "1rem",
  fontWeight: 600,
  color: "var(--color-text)",
};

type Similar = { slug: string; label: string } | null;

/** Yazılan boyut adına benzer boyut: yerel sonuç anında, sunucu sonucu gecikmeyle üstüne. */
export function useSimilarDimension(
  label: string,
  active: boolean,
  custom: readonly CustomDimensionOption[],
): Similar {
  const q = label.trim();
  const local = useMemo(() => (active ? similarHint(q, custom) : null), [active, q, custom]);
  const [remote, setRemote] = useState<{ q: string; hit: Similar } | null>(null);
  useEffect(() => {
    if (!active || q === "") return;
    let alive = true;
    const t = setTimeout(() => {
      listKpiBreakdownDimensions(q)
        .then((r) => {
          if (alive) setRemote({ q, hit: r.similar ?? null });
        })
        .catch(() => undefined);
    }, SIMILAR_DEBOUNCE_MS);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [active, q]);
  if (!active || q === "") return null;
  return remote?.q === q ? remote.hit : local;
}

export interface BreakdownDimensionPickerProps {
  form: GroupForm;
  rootCode: KpiRootCode | null;
  custom: readonly CustomDimensionOption[];
  /** aynı üstte zaten olan boyutlar — pasif */
  used: ReadonlySet<string>;
  disabled: boolean;
  onChoose: (choice: DimensionChoice) => void;
  onDimensionLabel: (value: string) => void;
  onSlug: (value: string) => void;
  /** liste açık başlar (SSR testleri) */
  initialOpen?: boolean | undefined;
  /** "Diğer boyutlar" açık başlar (varsayılan: uygun/özel bölüm doluyken kapalı — K-B6) */
  initialShowOther?: boolean | undefined;
}

export function BreakdownDimensionPicker(props: BreakdownDimensionPickerProps): JSX.Element {
  const { form, custom, used, disabled } = props;
  const [showOther, setShowOther] = useState(props.initialShowOther ?? false);
  const other = otherDimensionState(props.rootCode, custom, form.dimension);
  const otherOpen = !other.collapsible || showOther || other.forcedOpen;
  const options = useMemo(
    () => dimensionPickerOptions(props.rootCode, custom, used, { showOther: otherOpen }),
    [props.rootCode, custom, used, otherOpen],
  );
  const isOther = form.dimension === OTHER;
  const info = dimensionHelp(form, custom);
  const similar = useSimilarDimension(form.dimensionLabel, isOther, custom);
  const similarChoice: DimensionChoice | null = similar
    ? (choiceForValue(similar.slug, custom) ?? {
        kind: "custom",
        dim: { slug: similar.slug, label: similar.label, members: [] },
      })
    : null;

  return (
    <div style={fieldStyle}>
      <label style={question} htmlFor="bd-dimension">
        {C.dimensionQuestion}
      </label>
      <span style={help} data-testid="kpi-breakdown-dimension-intro">
        {C.dimensionIntro}
      </span>
      <SearchSelect
        id="bd-dimension"
        ariaLabel={C.dimensionQuestion}
        value={form.dimension}
        options={options}
        placeholder={C.dimensionPlaceholder}
        searchPlaceholder={C.dimensionSearch}
        disabled={disabled}
        showCode={false}
        initialOpen={props.initialOpen}
        onChange={(v) => {
          const choice = choiceForValue(v, custom);
          if (choice) props.onChoose(choice);
        }}
      />
      {other.collapsible && !other.forcedOpen ? (
        <button
          type="button"
          style={{ ...btnGhost, minHeight: 44, justifySelf: "start" }}
          aria-expanded={otherOpen}
          disabled={disabled}
          data-testid="kpi-breakdown-dimension-other-toggle"
          onClick={() => setShowOther(!otherOpen)}
        >
          {otherOpen ? C.hideOther : C.showOther(other.count)}
        </button>
      ) : null}
      {info ? (
        <span style={help} data-testid="kpi-breakdown-dimension-help">
          <span aria-hidden="true">ⓘ </span>
          {info.help} {info.example}
        </span>
      ) : null}
      {isOther ? (
        <>
          <label style={{ ...labelStyle, marginTop: 6 }} htmlFor="bd-dimension-name">
            {C.customNameLabel}
          </label>
          <input
            id="bd-dimension-name"
            style={inputStyle}
            value={form.dimensionLabel}
            placeholder={C.customNamePlaceholder}
            maxLength={80}
            disabled={disabled}
            onChange={(e) => props.onDimensionLabel(e.target.value)}
          />
          {similar && similarChoice ? (
            <div
              role="status"
              className="flex flex-wrap items-center gap-2"
              style={{ ...help, color: "var(--color-text-2)" }}
              data-testid="kpi-breakdown-similar"
            >
              <span>{C.similarDimension(similar.label)}</span>
              <button
                type="button"
                style={{ ...btnGhost, minHeight: 32, padding: "4px 10px" }}
                disabled={disabled || used.has(similar.slug)}
                onClick={() => props.onChoose(similarChoice)}
              >
                {C.useSimilar}
              </button>
            </div>
          ) : null}
          <label style={{ ...labelStyle, marginTop: 6 }} htmlFor="bd-slug">
            {C.customSlugLabel}
          </label>
          <input
            id="bd-slug"
            style={{ ...inputStyle, fontFamily: "ui-monospace, Menlo, Consolas, monospace" }}
            value={form.customSlug}
            maxLength={40}
            disabled={disabled}
            onChange={(e) => props.onSlug(e.target.value)}
          />
          <span style={help}>{C.customSlugHint}</span>
        </>
      ) : null}
    </div>
  );
}
